import React, { useEffect, useState, useCallback } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { Box, Button, Link, TextField, Typography } from '@mui/material';
import { getApiV1BaseUrl } from '../utils/apiBase';

export default function DataDeletionStatus() {
  const [searchParams] = useSearchParams();
  const initial = searchParams.get('code') || '';
  const [code, setCode] = useState(initial.trim());
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    document.title = 'Data deletion status — Nexus';
  }, []);

  const fetchStatus = useCallback(async (c) => {
    const trimmed = (c || '').trim();
    if (trimmed.length < 16) {
      setError('Enter the full confirmation code from Meta.');
      setResult(null);
      return;
    }
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const base = getApiV1BaseUrl();
      const url = `${base}/auth/facebook/data-deletion/status?code=${encodeURIComponent(trimmed)}`;
      const res = await fetch(url, { credentials: 'omit' });
      const data = await res.json();
      if (!res.ok && data.detail) {
        setError(typeof data.detail === 'string' ? data.detail : 'Request failed.');
        return;
      }
      setResult(data);
    } catch {
      setError('Could not reach the server. Try again later.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (initial.trim().length >= 16) fetchStatus(initial);
  }, [initial, fetchStatus]);

  return (
    <Box sx={{ maxWidth: 672, mx: 'auto', p: 3, pb: 6, color: 'text.primary' }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Data deletion request status
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Nexus · Digital Dynamic Solution
      </Typography>
      <Typography paragraph>
        Meta provides a <strong>confirmation code</strong> after you request deletion. Enter it below or
        open this page from the link Meta showed you (the code is in the URL).
      </Typography>

      <TextField
        fullWidth
        label="Confirmation code"
        value={code}
        onChange={(e) => setCode(e.target.value)}
        autoComplete="off"
        margin="normal"
        placeholder="Paste your code"
        disabled={loading}
      />
      <Button variant="contained" sx={{ mt: 1 }} disabled={loading} onClick={() => fetchStatus(code)}>
        {loading ? 'Checking…' : 'Check status'}
      </Button>

      {error ? (
        <Typography color="error" sx={{ mt: 2 }}>
          {error}
        </Typography>
      ) : null}

      {result ? (
        <Box sx={{ mt: 2 }}>
          <Typography sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
            Status: {result.status || 'unknown'}
          </Typography>
          {result.message ? <Typography paragraph>{result.message}</Typography> : null}
          {result.nexus_user_count != null && result.platform_accounts_removed != null ? (
            <Typography variant="body2" color="text.secondary">
              Details: {result.platform_accounts_removed} account record(s) removed across{' '}
              {result.nexus_user_count} workspace user(s).
            </Typography>
          ) : null}
        </Box>
      ) : null}

      <Typography variant="body2" color="text.secondary" sx={{ mt: 4 }}>
        For general deletion instructions, see{' '}
        <Link component={RouterLink} to="/data-deletion.html">
          User Data Deletion
        </Link>
        . Questions:{' '}
        <Link href="mailto:luthdigitalconsult@gmail.com">luthdigitalconsult@gmail.com</Link>
      </Typography>
    </Box>
  );
}
